import React, { createContext, useContext, useState, useEffect } from 'react';
import { storeAPI } from '../lib/api';

const BrandContext = createContext(null);

const LOG_PREFIX = '[BrandContext]';

// Default branding (fallback)
const DEFAULT_BRAND = {
  appName: 'AppLook',
  tagline: 'Prenota il tuo stile in un click',
  logoUrl: '',
  faviconUrl: '/favicon.ico',
  primaryColor: '#047857',
  secondaryColor: '#f3f4f6',
  footerText: '© 2025 AppLook. Tutti i diritti riservati.',
  socialLinks: {},
  meta: {
    title: 'AppLook - Prenotazioni Online',
    description: 'La piattaforma completa per barbieri e centri estetici.'
  }
};

export function BrandProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('multi-store'); // 'single-store' or 'multi-store'

  useEffect(() => {
    initBrand();
  }, []);

  const initBrand = async () => {
    try {
      // Load config.json
      const configResponse = await fetch('/config.json');
      const configData = await configResponse.json();
      setConfig(configData);
      setMode(configData.mode || 'multi-store');

      // Apply default branding from config
      if (configData.defaults) {
        setBrand(prev => ({ ...prev, ...configData.defaults }));
        applyBranding(configData.defaults);
        updateMetaTags(configData.defaults);
      }

      // For single-store mode, load store branding from backend
      if (configData.mode === 'single-store' && configData.storeSlug) {
        localStorage.setItem('storeSlug', configData.storeSlug);
        await loadStoreBranding(configData.storeSlug);
      }
    } catch (error) {
      console.error('Failed to load brand config:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStoreBranding = async (slug) => {
    try {
      const storeResponse = await storeAPI.getConfig(slug, { skipStoreSlug: true });
      const contentType = storeResponse.headers?.['content-type'] || '';
      const baseUrl = storeResponse.config?.baseURL || '';
      const url = storeResponse.config?.url || '';
      const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
      const isHtmlResponse = contentType.includes('text/html') || (
        typeof storeResponse.data === 'string' &&
        storeResponse.data.toLowerCase().includes('<!doctype html')
      );

      console.log(`${LOG_PREFIX} store-config response`, {
        status: storeResponse.status,
        url: fullUrl || url,
        contentType
      });

      if (isHtmlResponse) {
        throw new Error('API mismatch: received HTML');
      }

      const {
        store: storeData,
        branding: storeBranding,
        modules: storeModules
      } = storeResponse.data || {};
      const storeWithModules = storeData
        ? { ...storeData, modules: storeModules || [] }
        : null;
      setStore(storeWithModules);

      // Merge store branding with defaults
      if (storeData) {
        const brandingData = storeBranding || storeData.branding || {};
        const storeBrand = {
          appName: storeData.name || brand.appName,
          tagline: storeData.description || brand.tagline,
          logoUrl: brandingData.logo_url || brand.logoUrl,
          primaryColor: brandingData.primary_color || brand.primaryColor,
          secondaryColor: brandingData.secondary_color || brand.secondaryColor,
          footerText: `© ${new Date().getFullYear()} ${storeData.name}. Tutti i diritti riservati.`,
          socialLinks: brandingData.social_links || brand.socialLinks,
          meta: {
            title: `${storeData.name} - Prenota Online`,
            description: storeData.description || brand.meta.description
          }
        };
        setBrand(prev => ({ ...prev, ...storeBrand }));
        applyBranding(storeBrand);
        updateMetaTags(storeBrand);
      }
    } catch (error) {
      console.error('Failed to load store branding:', error);
    }
  };

  const applyBranding = (brandData) => {
    const root = document.documentElement;
    
    if (brandData.primaryColor) {
      const hsl = hexToHSL(brandData.primaryColor);
      if (hsl) {
        root.style.setProperty('--primary', hsl);
      }
    }

    // Update favicon if provided
    if (brandData.faviconUrl) {
      const favicon = document.querySelector("link[rel='icon']");
      if (favicon) {
        favicon.href = brandData.faviconUrl;
      }
    }
  };

  const updateMetaTags = (brandData) => {
    if (brandData.meta?.title) {
      document.title = brandData.meta.title;
    }
    
    const metaDescription = document.querySelector("meta[name='description']");
    if (metaDescription && brandData.meta?.description) {
      metaDescription.content = brandData.meta.description;
    }
  };

  const hexToHSL = (hex) => {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    
    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
        default: h = 0;
      }
    }
    
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  };

  // Refresh branding from backend (called after admin updates settings)
  const refreshBranding = async () => {
    if (config?.storeSlug) {
      await loadStoreBranding(config.storeSlug);
    }
  };

  return (
    <BrandContext.Provider value={{
      brand,
      store,
      config,
      mode,
      loading,
      isSingleStore: mode === 'single-store',
      isMultiStore: mode === 'multi-store',
      refreshBranding,
      storeSlug: config?.storeSlug || null
    }}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
}
